import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight, Ban, CheckCircle2, ClipboardList, Plus, Save, Scan, Search, X,
} from 'lucide-react'
import {
  completeStockTakeSchema, formatDateTime, formatMoney, formatNumber, PERMISSIONS,
  STOCK_TAKE_SCOPES, STOCK_TAKE_STATUS,
  type StockTakeDetail, type StockTakeItemRow, type StockTakeScope,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, ConfirmDialog, Dialog, EmptyState, Input,
  Select, Spinner, Switch, Table, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { categoriesApi } from '../lib/catalogApi'
import { stockTakeApi } from '../lib/stockTakeApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

const STATUS_TONE = {
  DRAFT: 'neutral',
  COUNTING: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
} as const

/* ── نافذة بدء جرد ── */
function NewStockTakeDialog({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, handleApiError } = useFormErrors()
  const [scope, setScope] = useState<StockTakeScope>('ALL')
  const [categoryId, setCategoryId] = useState('')
  const [note, setNote] = useState('')

  const { data: categories } = useQuery({
    queryKey: ['categories', 'options'],
    queryFn: () => categoriesApi.list(),
    enabled: open && scope === 'CATEGORY',
  })

  useEffect(() => {
    if (!open) return
    clear()
    setScope('ALL')
    setCategoryId('')
    setNote('')
  }, [open, clear])

  const mutation = useMutation({
    mutationFn: () => stockTakeApi.create({
      scope,
      categoryId: scope === 'CATEGORY' ? categoryId || null : null,
      note: note.trim(),
    }),
    onSuccess: (take) => {
      void queryClient.invalidateQueries({ queryKey: ['stock-takes'] })
      toast.success('بدأ الجرد', `${take.reference} — ${formatNumber(take.itemCount)} صنف`)
      onCreated(take.id)
      onClose()
    },
    onError: (err) => handleApiError(err, 'تعذّر بدء الجرد'),
  })

  const hint = STOCK_TAKE_SCOPES.find((s) => s.value === scope)?.hint

  return (
    <Dialog
      open={open} onClose={onClose} size="md"
      title="جرد جديد"
      description="يُجمَّد الرصيد الدفتري لكل صنف لحظة البدء"
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>ابدأ العدّ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <Select
          label="النطاق" required
          value={scope} onChange={(e) => setScope(e.target.value as StockTakeScope)}
          options={STOCK_TAKE_SCOPES.map((s) => ({ value: s.value, label: s.label }))}
          hint={hint}
        />

        {scope === 'CATEGORY' && (
          <Select
            label="التصنيف" required
            value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
            options={[{ value: '', label: 'اختر تصنيفاً' }, ...(categories ?? []).map((c) => ({ value: c.id, label: c.nameAr }))]}
            error={errors.categoryId}
            hint="يشمل التصنيفات الفرعية تحته"
          />
        )}

        <Input label="ملاحظة" value={note} onChange={(e) => setNote(e.target.value)} />

        <Alert tone="info">
          المكتبة تبقى تبيع أثناء العدّ. الفرق يُحسب على رصيد لحظة البدء،
          فمبيعات ساعات العدّ لا تُلغى عند الإقفال.
        </Alert>
      </div>
    </Dialog>
  )
}

/* ── نافذة الإقفال ── */
function CompleteDialog({ take, onClose }: { take: StockTakeDetail | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { formError, handleApiError } = useFormErrors()
  const [zeroUncounted, setZeroUncounted] = useState(false)
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!take) return
    setZeroUncounted(false)
    setNote('')
  }, [take])

  const uncounted = take ? take.itemCount - take.countedCount : 0

  const mutation = useMutation({
    mutationFn: () => stockTakeApi.complete(take!.id, completeStockTakeSchema.parse({
      uncounted: zeroUncounted ? 'ZERO' : 'SKIP',
      note: note.trim(),
    })),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['stock-takes'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('أُقفل الجرد', `سُوّي ${formatNumber(result.adjustedCount)} صنف`)
      onClose()
    },
    onError: (err) => handleApiError(err, 'تعذّر إقفال الجرد'),
  })

  return (
    <Dialog
      open={!!take} onClose={onClose} size="md"
      title="إقفال الجرد"
      description={take?.reference}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>أقفل وسوِّ الفروق</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        {take && (
          <div className="grid grid-cols-3 gap-3 rounded-lg bg-bg-subtle p-3 text-center">
            <div>
              <p className="text-xs text-fg-subtle">معدود</p>
              <p className="text-lg font-semibold tabular-nums text-fg">
                {formatNumber(take.countedCount)}<span className="text-sm text-fg-subtle">/{formatNumber(take.itemCount)}</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">به فرق</p>
              <p className="text-lg font-semibold tabular-nums text-fg">{formatNumber(take.varianceCount)}</p>
            </div>
            <div>
              <p className="text-xs text-fg-subtle">أثر الفروق</p>
              <p className={cn('text-lg font-semibold tabular-nums',
                take.varianceValue < 0 ? 'text-danger' : take.varianceValue > 0 ? 'text-success' : 'text-fg')} dir="ltr">
                {formatMoney(take.varianceValue)}
              </p>
            </div>
          </div>
        )}

        <Alert tone="warning">
          الإقفال يُسجّل حركة تسوية لكل صنف به فرق. الحركات لا تُحذف بعدها —
          التصحيح يكون بجرد جديد.
        </Alert>

        {uncounted > 0 && (
          <>
            <Switch
              checked={zeroUncounted}
              onChange={(e) => setZeroUncounted(e.target.checked)}
              label={`اعتبر الـ${formatNumber(uncounted)} صنفاً غير المعدود صفراً`}
              description="لا تفعل هذا إلا في جرد شامل عددتَ فيه كل رفّ فعلاً"
            />
            {zeroUncounted && (
              <Alert tone="danger">
                سيُصفَّر رصيد {formatNumber(uncounted)} صنف لم تُدخِل لها عدّاً.
                إن كان الجرد جزئياً فهذا يمحو مخزوناً موجوداً على الرفّ.
              </Alert>
            )}
          </>
        )}

        <Input label="ملاحظة الإقفال" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  )
}

/* ── شاشة العدّ ── */
function CountingView({ id, onBack }: { id: string; onBack: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const scanRef = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState('')
  const [onlyPending, setOnlyPending] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [completing, setCompleting] = useState<StockTakeDetail | null>(null)
  const [cancelling, setCancelling] = useState(false)

  const { data: take, isPending } = useQuery({
    queryKey: ['stock-takes', id],
    queryFn: () => stockTakeApi.findOne(id),
  })

  const isOpen = take?.status === 'COUNTING' || take?.status === 'DRAFT'

  useEffect(() => { setDrafts({}) }, [take?.countedCount, take?.status])

  const save = useMutation({
    mutationFn: (counts: { productId: string; countedQty: number | null }[]) =>
      stockTakeApi.recordCounts(id, { counts }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock-takes'] })
      setDrafts({})
      toast.success('حُفظ العدّ')
    },
    onError: (err) => toast.error('تعذّر حفظ العدّ', err instanceof Error ? err.message : undefined),
  })

  const cancel = useMutation({
    mutationFn: () => stockTakeApi.cancel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock-takes'] })
      toast.success('أُلغي الجرد', 'لم يتغيّر المخزون')
      onBack()
    },
    onError: (err) => toast.error('تعذّر الإلغاء', err instanceof Error ? err.message : undefined),
  })

  const rows = useMemo(() => {
    if (!take) return []
    const q = search.trim().toLowerCase()
    return take.items.filter((i) => {
      if (onlyPending && i.countedQty !== null && drafts[i.productId] === undefined) return false
      if (!q) return true
      return i.nameAr.toLowerCase().includes(q)
        || i.sku.toLowerCase().includes(q)
        || (i.barcode ?? '').toLowerCase().includes(q)
    })
  }, [take, search, onlyPending, drafts])

  /** الماسح يُرسل الرمز ثم Enter — نزيد عدّ الصنف المطابق بواحد ونُفرغ الحقل. */
  function onScan(e: React.FormEvent) {
    e.preventDefault()
    const code = search.trim()
    if (!code || !take) return
    const hit = take.items.find((i) => i.barcode === code || i.sku.toLowerCase() === code.toLowerCase())
    if (!hit) {
      toast.error('لا صنف بهذا الرمز في هذا الجرد', code)
      return
    }
    const current = drafts[hit.productId] !== undefined
      ? Number(drafts[hit.productId] || 0)
      : hit.countedQty ?? 0
    setDrafts((d) => ({ ...d, [hit.productId]: String(current + 1) }))
    setSearch('')
    scanRef.current?.focus()
  }

  function saveDrafts() {
    const counts = Object.entries(drafts).map(([productId, value]) => ({
      productId,
      countedQty: value === '' ? null : Number(value),
    }))
    if (counts.length) save.mutate(counts)
  }

  if (isPending || !take) {
    return <div className="grid h-64 place-items-center"><Spinner className="size-7 text-primary" /></div>
  }

  const dirty = Object.keys(drafts).length
  const progress = take.itemCount ? Math.round((take.countedCount / take.itemCount) * 100) : 0

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} title="رجوع">
            <ArrowRight className="size-5" />
          </Button>
          <div>
            <h1 className="font-display text-2xl font-bold text-fg" dir="ltr">{take.reference}</h1>
            <p className="mt-0.5 flex items-center gap-2 text-sm text-fg-muted">
              <Badge tone={STATUS_TONE[take.status]} dot>{STOCK_TAKE_STATUS[take.status]}</Badge>
              {take.storeName} · {take.userName ?? '—'} · {formatDateTime(take.startedAt)}
            </p>
          </div>
        </div>

        {isOpen && (
          <div className="flex gap-2">
            <Button variant="secondary" startIcon={<Ban className="size-4" />} onClick={() => setCancelling(true)}>
              إلغاء الجرد
            </Button>
            <Button
              startIcon={<CheckCircle2 className="size-4" />}
              onClick={() => setCompleting(take)}
              disabled={take.countedCount === 0}
            >
              إقفال وتسوية
            </Button>
          </div>
        )}
      </div>

      {/* شريط التقدّم */}
      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span className="text-fg-muted">
              عُدّ <span className="font-semibold tabular-nums text-fg">{formatNumber(take.countedCount)}</span>
              {' '}من <span className="tabular-nums">{formatNumber(take.itemCount)}</span> صنف
            </span>
            <span className="flex items-center gap-4">
              <span className="text-fg-muted">
                فروق: <span className="font-semibold tabular-nums text-fg">{formatNumber(take.varianceCount)}</span>
              </span>
              <span className={cn('font-semibold tabular-nums',
                take.varianceValue < 0 ? 'text-danger' : take.varianceValue > 0 ? 'text-success' : 'text-fg-muted')} dir="ltr">
                {formatMoney(take.varianceValue)}
              </span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-bg-subtle">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardBody>
      </Card>

      {isOpen && (
        <form onSubmit={onScan} className="flex flex-wrap items-end gap-3">
          <Input
            ref={scanRef}
            autoFocus
            className="min-w-64 flex-1"
            label="امسح باركود أو ابحث"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startIcon={<Scan className="size-4" />}
            hint="المسح يزيد عدّ الصنف بواحد؛ الكتابة ترشّح الجدول"
          />
          <Button type="submit" variant="secondary" startIcon={<Search className="size-4" />}>
            أضف واحداً
          </Button>
          {search && (
            <Button type="button" variant="ghost" size="icon" onClick={() => setSearch('')} title="مسح البحث">
              <X className="size-4" />
            </Button>
          )}
          <Switch
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
            label="غير المعدود فقط"
            className="pb-2"
          />
        </form>
      )}

      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>الصنف</Th>
              <Th>الرمز</Th>
              <Th align="center">الدفتري</Th>
              <Th align="center">المعدود</Th>
              <Th align="center">الفرق</Th>
              <Th align="end">أثر الفرق</Th>
            </tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <CountRow
                key={row.productId}
                row={row}
                draft={drafts[row.productId]}
                editable={isOpen}
                onChange={(v) => setDrafts((d) => ({ ...d, [row.productId]: v }))}
              />
            ))}
          </Tbody>
        </Table>
        {rows.length === 0 && (
          <EmptyState
            icon={<ClipboardList className="size-10" />}
            title={onlyPending ? 'كل الأصناف معدودة' : 'لا نتائج'}
          />
        )}
      </TableWrapper>

      {/* شريط حفظ ثابت — العدّ لا يُحفظ تلقائياً حتى لا يُسجَّل رقم نصف مكتوب */}
      {dirty > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur-md lg:ps-64">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <p className="text-sm text-fg-muted">
              {formatNumber(dirty)} صنفاً معدوداً لم يُحفظ
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setDrafts({})} disabled={save.isPending}>تراجع</Button>
              <Button onClick={saveDrafts} loading={save.isPending} startIcon={<Save className="size-4" />}>
                حفظ العدّ
              </Button>
            </div>
          </div>
        </div>
      )}

      <CompleteDialog take={completing} onClose={() => setCompleting(null)} />

      <ConfirmDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={() => cancel.mutate()}
        loading={cancel.isPending}
        title="إلغاء الجرد"
        confirmLabel="ألغِ الجرد"
        description="سيُلغى الجرد ويضيع ما عُدّ. المخزون لا يتغيّر."
      />
    </div>
  )
}

function CountRow({ row, draft, editable, onChange }: {
  row: StockTakeItemRow
  draft: string | undefined
  editable: boolean
  onChange: (value: string) => void
}) {
  const value = draft !== undefined ? draft : row.countedQty === null ? '' : String(row.countedQty)
  const counted = value === '' ? null : Number(value)
  const difference = counted === null ? null : counted - row.expectedQty
  const impact = difference === null ? null : difference * row.costPrice

  return (
    <Tr className={cn(counted === null && 'opacity-60')}>
      <Td>
        <p className="font-medium text-fg">{row.nameAr}</p>
        {row.categoryName && <p className="text-xs text-fg-subtle">{row.categoryName}</p>}
      </Td>
      <Td className="font-mono text-xs text-fg-muted" dir="ltr">{row.barcode ?? row.sku}</Td>
      <Td align="center" className="tabular-nums text-fg-muted">{formatNumber(row.expectedQty)}</Td>
      <Td align="center">
        {editable ? (
          <input
            type="number"
            min={0}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="—"
            dir="ltr"
            className={cn(
              'w-24 rounded-lg border bg-surface px-2 py-1.5 text-center tabular-nums text-fg',
              'focus:outline-2 focus:outline-ring focus:outline-offset-1',
              draft !== undefined ? 'border-primary' : 'border-border',
            )}
          />
        ) : (
          <span className="tabular-nums">{counted === null ? '—' : formatNumber(counted)}</span>
        )}
      </Td>
      <Td align="center">
        {difference === null ? (
          <span className="text-fg-subtle">—</span>
        ) : difference === 0 ? (
          <Badge tone="success">مطابق</Badge>
        ) : (
          <span className={cn('font-semibold tabular-nums', difference < 0 ? 'text-danger' : 'text-success')} dir="ltr">
            {difference > 0 ? '+' : ''}{formatNumber(difference)}
          </span>
        )}
      </Td>
      <Td align="end" className="tabular-nums text-fg-muted" dir="ltr">
        {impact === null || impact === 0 ? '—' : formatMoney(impact)}
      </Td>
    </Tr>
  )
}

/* ── القائمة ── */
export function StockTakePage() {
  const { can } = useAuth()
  const canAdjust = can(PERMISSIONS.STOCK_ADJUST)
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const { data, isPending } = useQuery({ queryKey: ['stock-takes'], queryFn: stockTakeApi.list })

  if (openId) return <CountingView id={openId} onBack={() => setOpenId(null)} />

  const active = data?.find((t) => t.status === 'COUNTING' || t.status === 'DRAFT')

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">الجرد</h1>
          <p className="mt-0.5 text-sm text-fg-muted">عدّ الرفوف وتسوية الفروق دفعة واحدة</p>
        </div>
        {canAdjust && (
          <Button
            startIcon={<Plus className="size-4" />}
            onClick={() => setCreating(true)}
            disabled={!!active}
            title={active ? 'أقفل الجرد المفتوح أولاً' : undefined}
          >
            جرد جديد
          </Button>
        )}
      </div>

      {active && (
        <Alert tone="warning" title={`جرد مفتوح: ${active.reference}`}>
          عُدّ {formatNumber(active.countedCount)} من {formatNumber(active.itemCount)} صنف.
          {' '}
          <button type="button" className="font-semibold underline" onClick={() => setOpenId(active.id)}>
            تابِع العدّ
          </button>
        </Alert>
      )}

      <Card>
        <CardHeader title="الجلسات" description="آخر ٥٠ جلسة" />
        <CardBody className="p-0">
          {isPending ? (
            <div className="grid h-40 place-items-center"><Spinner className="size-6 text-primary" /></div>
          ) : (
            <TableWrapper className="border-0">
              <Table>
                <Thead>
                  <tr>
                    <Th>الرقم</Th>
                    <Th>الفرع</Th>
                    <Th>بدأه</Th>
                    <Th align="center">التقدّم</Th>
                    <Th align="center">فروق</Th>
                    <Th align="center">الحالة</Th>
                    <Th>البداية</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {data?.map((t) => (
                    <Tr
                      key={t.id}
                      onClick={() => setOpenId(t.id)}
                      className="cursor-pointer"
                    >
                      <Td className="font-mono text-xs font-medium text-fg" dir="ltr">{t.reference}</Td>
                      <Td className="text-sm text-fg-muted">{t.storeName}</Td>
                      <Td className="text-sm text-fg-muted">{t.userName ?? '—'}</Td>
                      <Td align="center" className="tabular-nums text-sm">
                        {formatNumber(t.countedCount)}/{formatNumber(t.itemCount)}
                      </Td>
                      <Td align="center" className="tabular-nums text-sm">
                        {t.varianceCount > 0
                          ? <span className="font-semibold text-fg">{formatNumber(t.varianceCount)}</span>
                          : <span className="text-fg-subtle">—</span>}
                      </Td>
                      <Td align="center">
                        <Badge tone={STATUS_TONE[t.status]} dot>{STOCK_TAKE_STATUS[t.status]}</Badge>
                      </Td>
                      <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(t.startedAt)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {data?.length === 0 && (
                <EmptyState
                  icon={<ClipboardList className="size-10" />}
                  title="لا جرد بعد"
                  description="الجرد الدوري يكشف الفرق بين الرفّ والدفتر قبل أن يتراكم"
                />
              )}
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      <NewStockTakeDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={setOpenId}
      />
    </div>
  )
}
